import React, { useRef, useState, useCallback, useEffect } from 'react';
import { UploadIcon, MicrophoneIcon, XIcon, FileAudioIcon, WarningIcon, StopIcon, RecordIcon } from '@phosphor-icons/react';
import AudioPlayer from './AudioPlayer';

// Sample statements for recording - designed to bring out natural vocal tones and range (30-60 seconds each)
const SAMPLE_STATEMENTS = [
  // Warm storytelling with emotional range
  "When the sunlight strikes raindrops in the air, they act as a prism and form a rainbow. The rainbow is a division of white light into many beautiful colors. I remember the first time I truly understood that, standing in my grandmother's garden after a summer storm. She pointed up at the sky and said something I've never forgotten: that every beautiful thing in this world is just light, bending in ways we don't expect. I think about that a lot now, how the most ordinary things can become extraordinary if you just look at them differently.",

  // Reflective and philosophical
  "Have you ever wondered what it would be like to travel through time? I think about it sometimes, especially on quiet evenings like this one. Not to change anything, necessarily, but just to see. To stand in a moment that's already passed and watch it unfold again, knowing what I know now. There are conversations I'd listen to more carefully, sunsets I'd actually stop to watch instead of rushing past. But maybe that's the whole point of moving forward. Maybe we're not supposed to go back. Maybe we're just supposed to learn.",

  // Emotional warmth building to excitement
  "Oh, that's absolutely wonderful news! I'm so happy for you. We should definitely celebrate this together soon. You know, I was just thinking the other day about how far you've come. Do you remember when you first told me about this? You were so nervous, so unsure if it would even work out. And now look at you. This is exactly what you deserve, and I hope you take a moment to really appreciate it. Not everyone does, you know. Not everyone stops to feel the good moments. But you should. This is your moment.",

  // Nostalgic storytelling with sensory details
  "The old bookshop on the corner had been there for as long as anyone could remember. Its wooden shelves held stories from another era, and the smell of aging paper hung in the air like a promise. I used to spend hours there as a kid, tucked away in the back corner with a stack of books I couldn't afford to buy. The owner never seemed to mind. He'd just nod at me when I came in, like we had an understanding. I think he knew what those books meant to me. They were escape hatches, doorways to places I desperately wanted to go.",

  // Determined and resolute
  "I've thought about this for a long time, and I need you to understand where I'm coming from. This isn't a decision I made lightly. I weighed every option, considered every angle, and I keep arriving at the same conclusion. Sometimes the right path isn't the easy one. Sometimes it means walking away from things that feel safe because you know, deep down, that safe isn't the same as right. I'm ready for whatever comes next. I have to be. Because staying where I am isn't an option anymore.",

  // Gentle and comforting
  "You don't have to have it all figured out right now. Nobody does, even if they pretend otherwise. I spent so many years thinking everyone else had some secret manual for life that I never received. But the truth is, we're all just making it up as we go. And that's okay. That's actually more than okay. It means you get to decide what your story looks like. You get to write it, one day at a time, in whatever direction feels true to you. Just take it slow. You'll find your way.",
];

interface VoiceCloneUploaderProps {
  referenceAudio?: string;
  referenceTranscript?: string;
  referenceAudioDuration?: number;
  onAudioChange: (audioBase64: string | undefined) => void;
  onTranscriptChange: (transcript: string) => void;
  onDurationChange?: (duration: number) => void;
  className?: string;
  disabled?: boolean;
}

export const VoiceCloneUploader: React.FC<VoiceCloneUploaderProps> = ({
  referenceAudio,
  referenceTranscript = '',
  referenceAudioDuration = 0,
  onAudioChange,
  onTranscriptChange,
  onDurationChange,
  className = '',
  disabled = false,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const recordingTimerRef = useRef<NodeJS.Timeout | null>(null);
  const recordingTimeRef = useRef<number>(0); // Ref to track current recording time for onstop callback

  const [isDragging, setIsDragging] = useState(false);
  const [audioBlob, setAudioBlob] = useState<Blob | null>(null);
  const [audioDuration, setAudioDuration] = useState<number>(referenceAudioDuration);
  const [error, setError] = useState<string>('');
  const [audioSource, setAudioSource] = useState<'upload' | 'recording' | null>(
    referenceAudio ? 'upload' : null // Assume existing audio is uploaded (we don't know for sure)
  );

  // Recording state
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [showRecordingPanel, setShowRecordingPanel] = useState(false);
  const [currentStatement, setCurrentStatement] = useState(0);
  const [isCustomMode, setIsCustomMode] = useState(false);
  const [customStatement, setCustomStatement] = useState('');
  const customStatementRef = useRef(''); // Ref for onstop callback
  const isCustomModeRef = useRef(false); // Ref for onstop callback
  const MAX_RECORDING_TIME = 60; // seconds
  const MIN_RECORDING_TIME = 5; // minimum seconds before can stop
  const MIN_CUSTOM_CHARS = 450; // minimum characters for custom statement (~30 sec)

  // Keep refs in sync with state
  useEffect(() => {
    customStatementRef.current = customStatement;
    isCustomModeRef.current = isCustomMode;
  }, [customStatement, isCustomMode]);

  // Validate audio duration (5-15 seconds recommended)
  const validateAudio = useCallback(async (blob: Blob): Promise<boolean> => {
    return new Promise((resolve) => {
      const audio = new Audio();
      audio.onloadedmetadata = () => {
        const duration = audio.duration;
        setAudioDuration(duration);
        onDurationChange?.(duration);

        if (duration < 5) {
          setError('Audio is too short. Minimum 5 seconds required.');
          resolve(false);
        } else if (duration > 60) {
          setError('Audio is too long. Maximum 60 seconds allowed.');
          resolve(false);
        } else {
          setError('');
          resolve(true);
        }

        URL.revokeObjectURL(audio.src);
      };

      audio.onerror = () => {
        setError('Invalid audio file');
        resolve(false);
      };

      audio.src = URL.createObjectURL(blob);
    });
  }, [onDurationChange]);

  // Convert blob to base64
  const blobToBase64 = useCallback((blob: Blob): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64 = reader.result as string;
        resolve(base64);
      };
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  }, []);

  // Handle file selection
  const handleFile = useCallback(async (file: File) => {
    // Validate file type
    if (!file.type.startsWith('audio/')) {
      setError('Please select an audio file');
      return;
    }

    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      setError('File too large. Maximum 10MB allowed.');
      return;
    }

    const isValid = await validateAudio(file);
    if (!isValid) return;

    setAudioBlob(file);
    setAudioSource('upload');
    const base64 = await blobToBase64(file);
    onAudioChange(base64);
  }, [validateAudio, blobToBase64, onAudioChange]);

  // Drag and drop handlers
  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);

    const file = e.dataTransfer.files[0];
    if (file) handleFile(file);
  };

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) handleFile(file);
  };

  const handleRemove = () => {
    setAudioBlob(null);
    setAudioDuration(0);
    onDurationChange?.(0);
    setAudioSource(null);
    setError('');
    onAudioChange(undefined);
    onTranscriptChange('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Stop recording - check mediaRecorder state directly to avoid stale closure issues
  const stopRecording = useCallback(() => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
      mediaRecorderRef.current.stop();
      setIsRecording(false);

      if (recordingTimerRef.current) {
        clearInterval(recordingTimerRef.current);
        recordingTimerRef.current = null;
      }
    }
  }, []);

  // Start recording
  const startRecording = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: MediaRecorder.isTypeSupported('audio/webm') ? 'audio/webm' : 'audio/mp4'
      });

      audioChunksRef.current = [];
      mediaRecorderRef.current = mediaRecorder;

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = async () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: mediaRecorder.mimeType });

        // Stop all tracks
        stream.getTracks().forEach(track => track.stop());

        // For recorded audio, we know the duration from recordingTimeRef (3-20 seconds)
        // Skip validation since WebM blobs have unreliable duration metadata
        const recordedDuration = recordingTimeRef.current;
        setAudioDuration(recordedDuration);
        onDurationChange?.(recordedDuration);
        setAudioBlob(audioBlob);
        setAudioSource('recording');
        const base64 = await blobToBase64(audioBlob);
        onAudioChange(base64);
        // Auto-fill transcript with the statement they read
        const transcriptText = isCustomModeRef.current
          ? customStatementRef.current
          : SAMPLE_STATEMENTS[currentStatement];
        onTranscriptChange(transcriptText);
        setError('');

        setShowRecordingPanel(false);
      };

      mediaRecorder.start(100); // Collect data every 100ms
      setIsRecording(true);
      setRecordingTime(0);
      recordingTimeRef.current = 0;
      setError('');

      // Start timer - just increment, useEffect handles auto-stop
      recordingTimerRef.current = setInterval(() => {
        recordingTimeRef.current += 1; // Update ref for onstop callback
        setRecordingTime(prev => prev + 1);
      }, 1000);

    } catch (err) {
      console.error('Error accessing microphone:', err);
      setError('Could not access microphone. Please check permissions.');
    }
  }, [validateAudio, blobToBase64, onAudioChange, onTranscriptChange, currentStatement]);

  // Auto-stop when reaching max recording time
  useEffect(() => {
    if (isRecording && recordingTime >= MAX_RECORDING_TIME) {
      stopRecording();
    }
  }, [isRecording, recordingTime, stopRecording]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (recordingTimerRef.current) {
        clearInterval(recordingTimerRef.current);
      }
      if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
        mediaRecorderRef.current.stop();
      }
    };
  }, []);

  // Cycle to next statement
  const nextStatement = () => {
    setCurrentStatement((prev) => (prev + 1) % SAMPLE_STATEMENTS.length);
  };

  const isOptimalDuration = audioDuration >= 10 && audioDuration <= 45;

  return (
    <div className={className}>
      {/* Upload/Record Area */}
      {!audioBlob && !referenceAudio ? (
        showRecordingPanel ? (
          /* Recording Panel */
          <div className="border border-graphite rounded-xl bg-carbon overflow-hidden">
            {/* Header */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-graphite">
              <span className="text-sm font-medium text-white">Record Your Voice</span>
              <button
                onClick={() => {
                  if (isRecording) stopRecording();
                  setShowRecordingPanel(false);
                }}
                className="text-dust/40 hover:text-dust/60"
              >
                <XIcon className="w-4 h-4" />
              </button>
            </div>

            {/* Sample Statement or Custom Input */}
            <div className="p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs text-dust/40 uppercase tracking-wider">
                  {isCustomMode ? 'Your script:' : 'Read this aloud:'}
                </span>
                <div className="flex items-center gap-3">
                  {!isCustomMode ? (
                    <>
                      <button
                        onClick={() => setIsCustomMode(true)}
                        disabled={isRecording}
                        className="text-xs text-[#22c55e] hover:text-[#4ade80] disabled:opacity-50"
                      >
                        Write your own
                      </button>
                      <button
                        onClick={nextStatement}
                        disabled={isRecording}
                        className="text-xs text-slate-blue hover:text-[#9593FF] disabled:opacity-50"
                      >
                        Try another →
                      </button>
                    </>
                  ) : (
                    <button
                      onClick={() => setIsCustomMode(false)}
                      disabled={isRecording}
                      className="text-xs text-slate-blue hover:text-[#9593FF] disabled:opacity-50"
                    >
                      ← Use prompts
                    </button>
                  )}
                </div>
              </div>
              {isCustomMode ? (
                <div>
                  <textarea
                    value={customStatement}
                    onChange={(e) => setCustomStatement(e.target.value)}
                    disabled={isRecording}
                    placeholder="Write what you'll say in the recording. This helps the AI understand your voice better when cloning..."
                    className="w-full h-32 bg-onyx border border-graphite rounded-lg p-4 text-sm text-white focus:outline-none focus:border-slate-blue/50 resize-none placeholder:text-iron disabled:opacity-50"
                  />
                  <div className="flex items-center justify-between mt-1.5">
                    <p className="text-xs text-dust/30">
                      Write your own script to read aloud
                    </p>
                    <span className={`text-xs ${customStatement.length > 0 && customStatement.length < MIN_CUSTOM_CHARS ? 'text-amber-400/70' : 'text-dust/30'}`}>
                      {customStatement.length > 0 ? `${customStatement.length}/${MIN_CUSTOM_CHARS} min` : `${MIN_CUSTOM_CHARS} char min (~30 sec)`}
                    </span>
                  </div>
                </div>
              ) : (
                <p className="text-sm text-white leading-relaxed bg-onyx rounded-lg p-4 border border-graphite">
                  "{SAMPLE_STATEMENTS[currentStatement]}"
                </p>
              )}
            </div>

            {/* Recording Controls */}
            <div className="p-4 pt-0">
              <div className="flex flex-col items-center gap-4">
                {/* Timer */}
                <div className="flex items-center gap-3">
                  <div className={`w-2 h-2 rounded-full ${isRecording ? 'bg-red-500 animate-pulse' : 'bg-iron'}`} />
                  <span className={`text-2xl font-mono ${isRecording ? 'text-white' : 'text-iron'}`}>
                    {String(Math.floor(recordingTime / 60)).padStart(2, '0')}:{String(recordingTime % 60).padStart(2, '0')}
                  </span>
                  <span className="text-xs text-dust/40">/ 1:00</span>
                </div>

                {/* Progress Bar */}
                <div className="w-full h-1 bg-graphite rounded-full overflow-hidden">
                  <div
                    className="h-full bg-red-500 transition-[width] duration-1000 ease-linear"
                    style={{ width: `${(recordingTime / MAX_RECORDING_TIME) * 100}%` }}
                  />
                </div>

                {/* Record/Stop Button */}
                {isRecording ? (
                  <button
                    onClick={stopRecording}
                    disabled={disabled || recordingTime < MIN_RECORDING_TIME}
                    className={`w-16 h-16 rounded-full flex items-center justify-center transition-all ${
                      recordingTime < MIN_RECORDING_TIME
                        ? 'bg-red-500/50 cursor-not-allowed'
                        : 'bg-red-500 hover:bg-red-600'
                    }`}
                  >
                    <StopIcon className="w-6 h-6 text-white" weight="fill" />
                  </button>
                ) : (
                  <button
                    onClick={startRecording}
                    disabled={disabled || (isCustomMode && customStatement.length < MIN_CUSTOM_CHARS)}
                    className={`w-16 h-16 rounded-full flex items-center justify-center transition-all bg-slate-blue hover:bg-[#9593FF] ${
                      disabled || (isCustomMode && customStatement.length < MIN_CUSTOM_CHARS) ? 'opacity-50 cursor-not-allowed' : ''
                    }`}
                  >
                    <MicrophoneIcon className="w-6 h-6 text-white" />
                  </button>
                )}

                <p className="text-xs text-dust/40">
                  {isRecording
                    ? recordingTime < MIN_RECORDING_TIME
                      ? `Recording... (${MIN_RECORDING_TIME - recordingTime}s until you can stop)`
                      : 'Click to stop recording'
                    : isCustomMode && customStatement.length < MIN_CUSTOM_CHARS
                      ? `Write at least ${MIN_CUSTOM_CHARS} characters to start`
                      : 'Click to start recording (up to 60 seconds)'
                  }
                </p>
              </div>
            </div>
          </div>
        ) : (
          /* Upload/Record Choice */
          <div className="grid grid-cols-2 gap-3">
            {/* Upload Option */}
            <div
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
              className={`relative border-2 border-dashed rounded-xl p-6 text-center cursor-pointer transition-all ${
                isDragging
                  ? 'border-slate-blue bg-slate-blue/10'
                  : 'border-graphite bg-carbon hover:border-iron'
              } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept="audio/*"
                onChange={handleFileInput}
                disabled={disabled}
                className="hidden"
              />

              <div className="flex flex-col items-center gap-2">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                  isDragging ? 'bg-slate-blue/20' : 'bg-graphite'
                }`}>
                  <UploadIcon className={`w-5 h-5 ${isDragging ? 'text-slate-blue' : 'text-iron'}`} />
                </div>
                <div>
                  <p className="text-sm font-medium text-white">Upload File</p>
                  <p className="text-xs text-dust/40 mt-0.5">WAV, MP3, M4A</p>
                </div>
              </div>
            </div>

            {/* Record Option */}
            <button
              onClick={() => setShowRecordingPanel(true)}
              disabled={disabled}
              className={`border-2 border-dashed border-graphite bg-carbon hover:border-iron rounded-xl p-6 text-center transition-all ${
                disabled ? 'opacity-50 cursor-not-allowed' : ''
              }`}
            >
              <div className="flex flex-col items-center gap-2">
                <div className="w-10 h-10 rounded-full bg-graphite flex items-center justify-center">
                  <MicrophoneIcon className="w-5 h-5 text-iron" />
                </div>
                <div>
                  <p className="text-sm font-medium text-white">Record Voice</p>
                  <p className="text-xs text-dust/40 mt-0.5">Up to 60 seconds</p>
                </div>
              </div>
            </button>
          </div>
        )
      ) : (
        <div className="space-y-4">
          {/* Audio Preview */}
          <div className="relative">
            <AudioPlayer
              src={referenceAudio}
              blob={audioBlob || undefined}
              showWaveform
              knownDuration={audioDuration}
            />

            {/* Remove button */}
            <button
              onClick={handleRemove}
              disabled={disabled}
              className="absolute top-2 right-2 w-6 h-6 rounded-full bg-carbon/90 hover:bg-graphite flex items-center justify-center transition-colors"
            >
              <XIcon className="w-3.5 h-3.5 text-dust/60" />
            </button>
          </div>

          {/* Duration info */}
          <div className={`flex items-center gap-2 text-xs ${
            isOptimalDuration ? 'text-[#69F0AE]' : 'text-[#FFD740]'
          }`}>
            {!isOptimalDuration && <WarningIcon className="w-4 h-4" />}
            <span>
              Duration: {audioDuration.toFixed(1)}s
              {!isOptimalDuration && ' (10-45s recommended for best results)'}
            </span>
          </div>
        </div>
      )}

      {/* Error message */}
      {error && (
        <div className="mt-3 flex items-center gap-2 text-xs text-red-400">
          <WarningIcon className="w-4 h-4" />
          <span>{error}</span>
        </div>
      )}

      {/* Transcript Input - only show for uploaded files, not recordings (transcript auto-filled) */}
      {audioSource !== 'recording' && !showRecordingPanel && (
        <div className="mt-6">
          <label className="block text-xs text-dust/60 mb-2">
            Reference Transcript
            <span className="text-[#FFD740] ml-1">(Recommended)</span>
          </label>
          <textarea
            value={referenceTranscript}
            onChange={(e) => onTranscriptChange(e.target.value)}
            disabled={disabled}
            placeholder="Enter the exact text spoken in the reference audio for better voice cloning quality..."
            className="w-full h-24 bg-onyx border border-graphite rounded-xl p-3 text-sm text-white focus:outline-none focus:border-slate-blue/50 resize-none placeholder:text-iron disabled:opacity-50"
          />
          <p className="mt-2 text-xs text-dust/30">
            Providing the transcript helps the model better understand and clone the voice characteristics.
          </p>
        </div>
      )}
    </div>
  );
};

export default VoiceCloneUploader;
